/**
 * 공통 날짜 유틸
 * - 날짜 문자열/Date를 YYYY-MM-DD (요일) 등으로 포맷
 * - locale 미지정 시 i18n.language 기준으로 요일 표시 (ko/en)
 */

import i18n from './i18n';

/** 요일 한글 (일~토) */
export const DAY_NAMES_KO = ['일', '월', '화', '수', '목', '금', '토'];

/** 요일 영문 약어 (Sun~Sat) */
export const DAY_NAMES_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function resolveLocaleLanguage(locale) {
  const lang = String(locale || 'ko').split('-')[0].toLowerCase();
  return lang === 'en' ? 'en' : 'ko';
}

/** locale 미지정 시 react-i18next 현재 언어 사용 */
export function resolveActiveDateLocale(locale) {
  if (locale !== undefined && locale !== null && String(locale).trim() !== '') {
    return resolveLocaleLanguage(locale);
  }
  try {
    if (i18n?.language) return resolveLocaleLanguage(i18n.language);
  } catch {
    /* i18n 미초기화(테스트 등) */
  }
  return 'ko';
}

/**
 * locale 기준 요일명 (일~토 / Sun~Sat)
 * @param {string|Date|number} value
 * @param {string} [locale]
 */
export function getDayNameForLocale(value, locale) {
  const d = value instanceof Date
    ? (Number.isNaN(value.getTime()) ? null : value)
    : parseDate(value);
  if (!d) return '';
  const names = resolveActiveDateLocale(locale) === 'en' ? DAY_NAMES_EN : DAY_NAMES_KO;
  return names[d.getDay()];
}

/**
 * 다양한 입력을 Date 객체로 변환 (실패 시 null)
 * @param {string|Date|number} value - ISO 문자열, "YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss", Date, 타임스탬프
 * @returns {Date|null}
 */
export function parseDate(value) {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const str = String(value).trim();
  if (!str) return null;
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * 날짜를 YYYY-MM-DD 문자열로 반환
 * @param {string|Date|number} value
 * @returns {string} "YYYY-MM-DD" 또는 빈 문자열
 */
export function toDateOnly(value) {
  const d = parseDate(value);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * 주간 차트 X축 라벨이 `YYYY-MM-DD`(해당 주 월요일, PG `date_trunc('week')`와 동일)일 때
 * 툴팁용 `YYYY-MM-DD ~ YYYY-MM-DD`(월~일) 구간 문자열.
 * @param {string} weekStartLabel
 * @returns {string}
 */
export function formatWeekRangeFromMondayLabel(weekStartLabel) {
  if (weekStartLabel == null || String(weekStartLabel).trim() === '') return '';
  const s = String(weekStartLabel).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10) - 1;
  const d = parseInt(m[3], 10);
  const start = new Date(y, mo, d);
  if (Number.isNaN(start.getTime())) return s;
  const end = new Date(y, mo, d);
  end.setDate(end.getDate() + 6);
  const fmt = (dt) => {
    const yy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  };
  return `${fmt(start)} ~ ${fmt(end)}`;
}

/**
 * 요일 인덱스(0~6)에 해당하는 한글 요일명 반환
 * @param {number} dayIndex - 0=일, 6=토
 * @returns {string}
 */
export function getDayNameKoByIndex(dayIndex) {
  const i = Number(dayIndex);
  if (Number.isNaN(i) || i < 0 || i > 6) return '';
  return DAY_NAMES_KO[i];
}

/**
 * 날짜의 한글 요일명 반환 (일~토)
 * @param {string|Date|number} value
 * @returns {string}
 */
export function getDayNameKo(value) {
  return getDayNameForLocale(value, 'ko');
}

/** 현재 i18n 언어 기준 요일명 */
export function getDayName(value, locale) {
  return getDayNameForLocale(value, locale);
}

/**
 * 날짜를 "YYYY-MM-DD (요일)" 형식으로 반환
 * @param {string|Date|number} value
 * @param {string} [locale] - 미지정 시 i18n.language
 */
export function formatDateWithWeekday(value, locale) {
  if (value == null || value === '') return '';
  const activeLocale = resolveActiveDateLocale(locale);
  const d = parseDate(value);
  if (!d) {
    const str = String(value).trim();
    const datePart = str.split(' ')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
    return str;
  }
  const dateStr = toDateOnly(d);
  const dayName = getDayNameForLocale(d, activeLocale);
  return `${dateStr} (${dayName})`;
}

/** NTIS 등에서 미제공 시 내려오는 0001-01-01 류 플레이스홀더를 UI에서 쓰는 문구 */
export const NTIS_DATE_MISSING_LABEL = '정보 없음';

export function getNtisDateMissingLabel(locale) {
  try {
    if (i18n?.isInitialized) {
      return i18n.t('common.date.noInfo');
    }
  } catch {
    /* ignore */
  }
  return resolveActiveDateLocale(locale) === 'en' ? 'No information' : NTIS_DATE_MISSING_LABEL;
}

/**
 * NTIS 연계 접수/마감 필드가 실질적으로 비어 있는지 (null·빈문자, 파싱불가, 연도 ≤ 1·0001-01-01)
 */
export function isMissingNtisDate(raw) {
  if (raw == null || String(raw).trim() === '') return true;
  const datePart = String(raw).trim().split(/[T\s]/)[0];
  if (/^0*1-0*1-0*1$/i.test(datePart)) return true;
  const d = parseDate(raw);
  if (!d || Number.isNaN(d.getTime())) return true;
  if (d.getFullYear() <= 1) return true;
  return false;
}

/**
 * NTIS 날짜 1개 표시: 유효하면 formatDateWithWeekday, 없으면 missingLabel
 */
export function formatNtisDateWithWeekday(raw, missingLabel, locale) {
  const label = missingLabel ?? getNtisDateMissingLabel(locale);
  if (isMissingNtisDate(raw)) return label;
  return formatDateWithWeekday(raw, locale);
}

/**
 * 날짜·시간을 "YYYY-MM-DD (요일) HH:mm:ss" 형식으로 반환
 */
export function formatDateTimeWithWeekday(value, locale) {
  if (value == null || value === '') return '';
  const activeLocale = resolveActiveDateLocale(locale);
  let d = parseDate(value);
  if (!d && typeof value === 'string') {
    const s = value.trim().replace(' ', 'T');
    d = parseDate(s.length === 10 ? `${s}T00:00:00` : s);
  }
  if (!d) {
    const str = String(value).trim();
    return str || '';
  }
  const dateStr = toDateOnly(d);
  const dayName = getDayNameForLocale(d, activeLocale);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${dateStr} (${dayName}) ${hh}:${mm}:${ss}`;
}

/**
 * 관리자 사용자 관리(목록·상세): 가입일 — YYYY.MM.DD(요일) HH:mm:ss
 */
export function formatAdminUserRegDt(value, locale) {
  if (value == null || value === '') return '-';
  const s = formatDateTimeWithWeekday(value, locale);
  if (!s) return '-';
  return s.replace(/^(\d{4})-(\d{2})-(\d{2}) \(([^)]+)\) /, '$1.$2.$3($4) ');
}

/**
 * 원본 문자열 기준으로 시각 초를 생략할지 (없음·:00·.000만) — 제안일시 UI용
 * @param {string} originalStr
 * @param {Date} parsed
 */
function shouldOmitTimeSeconds(originalStr, parsed) {
  const m = String(originalStr).match(/(?:T|\s)(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?/);
  if (m) {
    if (m[3] == null || m[3] === '') return true;
    if (m[3] === '00' && (!m[4] || /^0+$/.test(m[4]))) return true;
    return false;
  }
  return parsed.getSeconds() === 0 && parsed.getMilliseconds() === 0;
}

/**
 * 날짜·시간: 초 정보가 없거나 :00(및 .0…)이면 "… HH:mm", 아니면 "… HH:mm:ss"
 * @param {string|Date|number} value
 */
export function formatDateTimeWithWeekdaySmartSeconds(value, locale) {
  if (value == null || value === '') return '';
  const activeLocale = resolveActiveDateLocale(locale);
  const original = typeof value === 'string' ? value.trim() : value;
  let d = parseDate(value);
  if (!d && typeof value === 'string') {
    const s0 = String(value).trim().replace(' ', 'T');
    d = parseDate(s0.length === 10 ? `${s0}T00:00:00` : s0);
  }
  if (!d) {
    const str = String(value).trim();
    return str || '';
  }
  const dateStr = toDateOnly(d);
  const dayName = getDayNameForLocale(d, activeLocale);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const origStr = typeof original === 'string' ? original : '';
  if (origStr && shouldOmitTimeSeconds(origStr, d)) {
    return `${dateStr} (${dayName}) ${hh}:${mm}`;
  }
  if (!origStr && shouldOmitTimeSeconds('', d)) {
    return `${dateStr} (${dayName}) ${hh}:${mm}`;
  }
  return `${dateStr} (${dayName}) ${hh}:${mm}:${ss}`;
}

/**
 * 제안 적용일시 표시 (받은 제안 카드·상세)
 * - 값이 비면: 목록은 빈 문자열, 상세는 두 번째 인자로 `formatDateWithWeekday` 폴백(원복)
 * - 문자열에 시각(HH:mm)이 없으면 날짜+요일만 (시간 강제 표시로 00:00:00 나오는 것 방지)
 * - 시각이 있으면 `formatDateTimeWithWeekdaySmartSeconds` (초 없음·:00이면 HH:mm만)
 *
 * @param {string|Date|number|null|undefined} value
 * @param {string|Date|number|null|undefined} [fallbackWhenEmpty] - value가 비었을 때만 사용
 */
export function formatProposalAppliedAtDisplay(value, fallbackWhenEmpty, locale) {
  const activeLocale = resolveActiveDateLocale(locale);
  if (value == null || String(value).trim() === '') {
    if (fallbackWhenEmpty == null || String(fallbackWhenEmpty).trim() === '') {
      return '';
    }
    return formatDateWithWeekday(fallbackWhenEmpty, activeLocale);
  }
  const s = String(value).trim();
  const hasClockTime = /\d{4}-\d{2}-\d{2}(?:T|\s+)[0-9]{1,2}:[0-9]{2}/.test(s);
  if (!hasClockTime) {
    const datePart = s.split(/[T\s]/)[0];
    if (datePart && /^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return formatDateWithWeekday(datePart, activeLocale);
    }
    return formatDateWithWeekday(s, activeLocale);
  }
  return formatDateTimeWithWeekdaySmartSeconds(s, activeLocale);
}
